import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SbomService } from '../../../Services/sbom.service';
import {
  SbomProject,
  SecurityAssessmentCreate,
  SecurityRequirementCreate,
  FOUNDATIONAL_REQUIREMENTS,
  NIS2_MEASURES,
  DOCUMENTS_CHECKLIST,
  SL_DESCRIPTIONS,
  ASSESSMENT_PHASES
} from '../../../Models/sbom.model';

@Component({
  selector: 'app-security-assessment',
  templateUrl: './security-assessment.component.html',
  styleUrls: ['./security-assessment.component.css']
})
export class SecurityAssessmentComponent implements OnInit {
  projectId = 0;
  project: SbomProject | null = null;
  loading = true;
  saving = false;
  phase = 0;

  phases = ASSESSMENT_PHASES;
  frData = FOUNDATIONAL_REQUIREMENTS;
  nis2Measures = NIS2_MEASURES;
  docsChecklist = DOCUMENTS_CHECKLIST;
  slDescriptions = SL_DESCRIPTIONS;
  slKeys = [0, 1, 2, 3, 4];

  // Wizard state
  answers: Record<string, any> = {};
  frAnswers: Record<string, string> = {};
  docChecks: Record<string, boolean> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sbomService: SbomService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.projectId = Number(this.route.snapshot.paramMap.get('id'));
    if (this.projectId) {
      this.loadProject();
    }
  }

  loadProject(): void {
    this.loading = true;
    this.sbomService.getProject(this.projectId).subscribe({
      next: (project) => {
        this.project = project;
        // Load existing assessment if available
        if (project.securityAssessment) {
          const a = project.securityAssessment;
          try {
            this.answers = { ...JSON.parse(a.contextAnswers), ...JSON.parse(a.nis2Answers) };
            this.answers['target_sl'] = String(a.targetSecurityLevel);
            this.frAnswers = JSON.parse(a.frAnswers);
            this.docChecks = JSON.parse(a.docChecks);
          } catch {
            // If parse fails, start fresh
          }
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Projekt nicht gefunden', 'OK', { duration: 4000 });
        this.router.navigate(['/sbom']);
        this.loading = false;
      }
    });
  }

  get targetSL(): number {
    return parseInt(this.answers['target_sl'] || '0', 10);
  }

  get achievedSL(): number {
    return this.calculateAchievedSL();
  }

  get gap(): number {
    return Math.max(0, this.targetSL - this.achievedSL);
  }

  get filteredDocs(): typeof DOCUMENTS_CHECKLIST {
    return this.docsChecklist.filter(d => d.required_sl <= this.targetSL || d.required_sl <= 1);
  }

  get nis2Count(): number {
    return this.nis2Measures.filter(m => this.answers[m.id]).length;
  }

  get docCount(): number {
    return Object.values(this.docChecks).filter(Boolean).length;
  }

  get totalDocs(): number {
    return this.filteredDocs.length;
  }

  get missingDocs(): typeof DOCUMENTS_CHECKLIST {
    return this.filteredDocs.filter(d => !this.docChecks[d.id]);
  }

  setAnswer(key: string, value: any): void {
    this.answers = { ...this.answers, [key]: value };
  }

  setFrAnswer(questionId: string, value: string): void {
    this.frAnswers = { ...this.frAnswers, [questionId]: value };
  }

  toggleDoc(docId: string): void {
    this.docChecks = { ...this.docChecks, [docId]: !this.docChecks[docId] };
  }

  nextPhase(): void {
    if (this.canProceed() && this.phase < this.phases.length - 1) {
      this.phase++;
    }
  }

  prevPhase(): void {
    if (this.phase > 0) this.phase--;
  }

  canProceed(): boolean {
    switch (this.phase) {
      case 0: return true; // Welcome
      case 1: // Projektkontext
        return !!this.answers['company_type'];
      case 2: return true; // NIS2 (optional)
      case 3: return true; // Zones (optional)
      case 4: // Security Level
        return this.answers['target_sl'] !== undefined && this.answers['target_sl'] !== '';
      case 5: return true; // FR
      case 6: return true; // Docs
      default: return true;
    }
  }

  calculateAchievedSL(): number {
    let minSL = 4;
    for (const fr of this.frData) {
      let frMaxSL = 0;
      for (const q of fr.questions) {
        const ans = this.frAnswers[q.id];
        if (ans === 'yes') {
          frMaxSL = Math.max(frMaxSL, q.sl_relevant);
        } else if (ans === 'partial') {
          frMaxSL = Math.max(frMaxSL, Math.max(0, q.sl_relevant - 1));
        }
      }
      minSL = Math.min(minSL, frMaxSL);
    }
    // If no FR answers at all, SL is 0
    const hasAnyAnswer = Object.keys(this.frAnswers).length > 0;
    return hasAnyAnswer ? minSL : 0;
  }

  getFrPercentage(fr: typeof FOUNDATIONAL_REQUIREMENTS[0]): number {
    const total = fr.questions.length;
    if (total === 0) return 0;
    const yes = fr.questions.filter(q => this.frAnswers[q.id] === 'yes').length;
    const partial = fr.questions.filter(q => this.frAnswers[q.id] === 'partial').length;
    return Math.round(((yes + partial * 0.5) / total) * 100);
  }

  generateRequirements(): SecurityRequirementCreate[] {
    const requirements: SecurityRequirementCreate[] = [];

    // Missing NIS2 measures
    for (const m of this.nis2Measures) {
      if (!this.answers[m.id]) {
        requirements.push({
          category: 'CRA',
          requirementId: m.id,
          title: m.text,
          description: `CRA/IEC 62443 Anforderung offen: ${m.text}`,
          normReference: m.article,
          priority: 1
        });
      }
    }

    // Failed FR questions
    for (const fr of this.frData) {
      for (const q of fr.questions) {
        if (q.sl_relevant <= this.targetSL && this.frAnswers[q.id] !== 'yes') {
          requirements.push({
            category: 'FR',
            requirementId: q.id,
            title: q.text,
            description: `${fr.id} ${fr.nameDe}: ${this.frAnswers[q.id] === 'partial' ? 'Teilweise umgesetzt' : 'Nicht umgesetzt'}`,
            normReference: `${fr.id} (IEC 62443-3-3), ab SL ${q.sl_relevant}`,
            priority: this.frAnswers[q.id] === 'partial' ? 2 : 1
          });
        }
      }
    }

    // Missing documents
    for (const d of this.filteredDocs) {
      if (!this.docChecks[d.id]) {
        requirements.push({
          category: 'Dokument',
          requirementId: d.id,
          title: d.text,
          description: `Dokument fehlt: ${d.text}`,
          normReference: d.norm,
          priority: d.required_sl <= 1 ? 1 : 2
        });
      }
    }

    return requirements;
  }

  saveAssessment(): void {
    if (!this.project) return;

    this.saving = true;
    const contextKeys = ['company_type', 'sector', 'customer_security_req', 'has_network_components'];
    const contextAnswers: Record<string, any> = {};
    const nis2Answers: Record<string, any> = {};

    for (const [key, val] of Object.entries(this.answers)) {
      if (contextKeys.includes(key) || key === 'target_sl') {
        contextAnswers[key] = val;
      } else {
        nis2Answers[key] = val;
      }
    }
    // Also include zone answers in context
    const zoneKeys = ['has_network_segmentation', 'has_dmz', 'has_firewall_rules', 'has_zone_diagram'];
    for (const k of zoneKeys) {
      if (this.answers[k] !== undefined) {
        contextAnswers[k] = this.answers[k];
      }
    }

    const dto: SecurityAssessmentCreate = {
      contextAnswers: JSON.stringify(contextAnswers),
      nis2Answers: JSON.stringify(nis2Answers),
      frAnswers: JSON.stringify(this.frAnswers),
      docChecks: JSON.stringify(this.docChecks),
      targetSecurityLevel: this.targetSL,
      achievedSecurityLevel: this.achievedSL,
      requirements: this.generateRequirements()
    };

    this.sbomService.saveSecurityAssessment(this.projectId, dto).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Sicherheitsbewertung gespeichert', 'OK', { duration: 3000 });
        this.router.navigate(['/sbom/projekt', this.projectId]);
      },
      error: () => {
        this.saving = false;
        this.snackBar.open('Fehler beim Speichern', 'OK', { duration: 4000 });
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/sbom/projekt', this.projectId]);
  }
}
